import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import BaseFlatpickr from "react-flatpickr";

type DatePickerMode = "single" | "range" | "multiple";
type DateValue = string | Date | null | undefined;
type DateArrayValue = Array<string | Date>;

type CompatOnChange = (
  selectedDates: Date[],
  dateStr: string,
  instance: unknown
) => void;

interface CommonDatePickerOptions {
  mode?: DatePickerMode;
  defaultDate?: DateValue | DateArrayValue;
  minDate?: DateValue;
  maxDate?: DateValue;
  [key: string]: unknown;
}

interface CommonDatePickerProps {
  mode?: DatePickerMode;
  value?: DateValue | DateArrayValue;
  onChangeDates?: (dates: Date[]) => void;
  onChangeValue?: (value: string) => void;
  onChange?: CompatOnChange;
  onClear?: () => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  clearButtonLabel?: string;
  minDate?: DateValue;
  maxDate?: DateValue;
  options?: CommonDatePickerOptions;
  style?: React.CSSProperties;
  size?: string | number;
}

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const isValidDate = (value: Date | null): value is Date => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

const toLocalDate = (value: DateValue): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isValidDate(value) ? value : null;

  const matched = value.match(DATE_ONLY_REGEX);
  if (matched) {
    const year = Number(matched[1]);
    const month = Number(matched[2]);
    const day = Number(matched[3]);
    const date = new Date(year, month - 1, day);
    return isValidDate(date) ? date : null;
  }

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

const pad2 = (value: number): string => String(value).padStart(2, "0");

const formatLocalDate = (date: Date): string => {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const normalizeDateArray = (value: DateValue | DateArrayValue): Date[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => toLocalDate(item))
      .filter((item): item is Date => isValidDate(item));
  }

  if (typeof value === "string" && value.includes(" to ")) {
    return value
      .split(" to ")
      .map((item) => toLocalDate(item.trim()))
      .filter((item): item is Date => isValidDate(item));
  }

  const single = toLocalDate(value);
  return isValidDate(single) ? [single] : [];
};

const resolvePickerValue = (
  mode: DatePickerMode,
  value: DateValue | DateArrayValue
): Date | Date[] | undefined => {
  const normalized = normalizeDateArray(value);

  if (mode === "single") {
    return normalized[0];
  }

  return normalized;
};

const CommonDatePicker = ({
  mode,
  value,
  onChangeDates,
  onChangeValue,
  onChange,
  onClear,
  id,
  className = "form-control",
  placeholder,
  disabled,
  clearable = false,
  clearButtonLabel = "Clear date",
  minDate,
  maxDate,
  options,
  style,
  size,
}: CommonDatePickerProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [renderCalendarInline, setRenderCalendarInline] = useState(false);
  const resolvedMode: DatePickerMode = mode ?? options?.mode ?? "single";
  const normalizedValue = useMemo(() => normalizeDateArray(value), [value]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    setRenderCalendarInline(Boolean(wrapper.closest('[role="dialog"]')));
  }, []);

  const pickerValue = useMemo(
    () => resolvePickerValue(resolvedMode, value),
    [resolvedMode, value]
  );

  const pickerOptions = useMemo(() => {
    const baseOptions = { ...(options ?? {}) };
    const resolvedMinDate = minDate ?? options?.minDate;
    const resolvedMaxDate = maxDate ?? options?.maxDate;
    const resolvedDefaultDate = options?.defaultDate;

    if (resolvedMinDate !== undefined) {
      baseOptions.minDate = resolvedMinDate === "today" ? "today" : toLocalDate(resolvedMinDate);
    }

    if (resolvedMaxDate !== undefined) {
      baseOptions.maxDate = toLocalDate(resolvedMaxDate);
    }

    if (resolvedDefaultDate !== undefined) {
      if (resolvedMode === "single") {
        const firstDate = normalizeDateArray(resolvedDefaultDate)[0] ?? undefined;
        baseOptions.defaultDate = firstDate;
      } else {
        baseOptions.defaultDate = normalizeDateArray(resolvedDefaultDate);
      }
    }

    return {
      ...baseOptions,
      static: baseOptions.static ?? renderCalendarInline,
      mode: resolvedMode,
      dateFormat: "Y-m-d",
      altInput: baseOptions.altInput ?? false,
      allowInput: false,
    };
  }, [maxDate, minDate, options, renderCalendarInline, resolvedMode]);

  const handleChange: CompatOnChange = (selectedDates, dateStr, instance) => {
    onChangeDates?.(selectedDates);
    onChange?.(selectedDates, dateStr, instance);

    if (resolvedMode === "single" && onChangeValue) {
      onChangeValue(selectedDates[0] ? formatLocalDate(selectedDates[0]) : "");
    }
  };

  const handleClearClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (onClear) {
      onClear();
      return;
    }

    onChangeDates?.([]);
    onChange?.([], "", null);

    if (resolvedMode === "single") {
      onChangeValue?.("");
    }
  };

  const showClearButton = clearable && !disabled && normalizedValue.length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full">
      <BaseFlatpickr
        key={renderCalendarInline ? "modal-inline" : "default-popup"}
        id={id}
        className={className}
        value={pickerValue}
        onChange={handleChange}
        options={pickerOptions}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
        size={size}
      />
      {showClearButton ? (
        <button
          type="button"
          className="btn btn-sm btn-soft-secondary position-absolute"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={handleClearClick}
          aria-label={clearButtonLabel}
          title={clearButtonLabel}
          style={{
            top: "50%",
            right: "4px",
            transform: "translateY(-50%)",
            lineHeight: 1,
            padding: "2px 6px",
            zIndex: 4,
          }}
        >
          <i className="ri-close-line" />
        </button>
      ) : null}
    </div>
  );
};

export default CommonDatePicker;
